'use client';

import { useState } from 'react';
import { Calculator, DollarSign, Percent, Calendar, TrendingUp, TrendingDown, Info, RotateCcw } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { emiCalculatorContent } from '../../data/emi-calculator-content';
import Link from 'next/link';

export default function EMICalculator() {
  const [formData, setFormData] = useState({
    loanAmount: '10000',  // Default loan amount
    interestRate: '8.5',  // Default interest rate
    loanTenure: '5',      // Default loan tenure in years
    loanTenureType: 'years', // 'years' or 'months'
    emiPayment: 'EMI'     // 'EMI' or 'Advance EMI'
  });

  const [results, setResults] = useState({
    emi: 0,
    totalInterest: 0,
    totalPayment: 0,
    showResults: false
  });

  const calculateEMI = () => {
    const principal = parseFloat(formData.loanAmount);
    const rate = parseFloat(formData.interestRate) / 12 / 100; // Monthly interest rate
    let n = formData.loanTenureType === 'years'
      ? parseFloat(formData.loanTenure) * 12
      : parseFloat(formData.loanTenure);

    // Calculate EMI
    const emi = principal * rate * Math.pow(1 + rate, n) / (Math.pow(1 + rate, n) - 1);

    // For Advance EMI (pay at the beginning of the month)
    const adjustedEMI = formData.emiPayment === 'Advance EMI'
      ? emi / (1 + rate)
      : emi;

    const totalPayment = adjustedEMI * n;
    const totalInterest = totalPayment - principal;

    setResults({
      emi: adjustedEMI,
      totalInterest,
      totalPayment,
      showResults: true
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const resetForm = () => {
    setFormData({
      loanAmount: '10000',
      interestRate: '8.5',
      loanTenure: '5',
      loanTenureType: 'years',
      emiPayment: 'EMI'
    });
    setResults({
      emi: 0,
      totalInterest: 0,
      totalPayment: 0,
      showResults: false
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <ToolPageIntro title="EMI Calculator">
                Calculate Equated Monthly Installments (EMI) for loans with our free online EMI calculator. Plan your loan repayments and understand total interest costs.
            </ToolPageIntro>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Loan Details
            </h2>

            <div className="space-y-6">
              <div>
                <label htmlFor="loanAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Loan Amount (₹)
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="loanAmount"
                    id="loanAmount"
                    value={formData.loanAmount}
                    onChange={handleInputChange}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="100,000"
                    min="1000"
                    step="1000"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Interest Rate (% p.a.)
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Percent className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="interestRate"
                    id="interestRate"
                    value={formData.interestRate}
                    onChange={handleInputChange}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="8.5"
                    min="0.1"
                    step="0.1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="loanTenure" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Loan Tenure
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <input
                      type="number"
                      name="loanTenure"
                      id="loanTenure"
                      value={formData.loanTenure}
                      onChange={handleInputChange}
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-3 pr-12 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      min="1"
                      step="1"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <select
                        name="loanTenureType"
                        value={formData.loanTenureType}
                        onChange={handleInputChange}
                        className="focus:ring-blue-500 focus:border-blue-500 h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 dark:text-gray-400 sm:text-sm rounded-r-md"
                      >
                        <option value="years">Years</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    EMI Payment
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        id="emi-normal"
                        name="emiPayment"
                        type="radio"
                        value="EMI"
                        checked={formData.emiPayment === 'EMI'}
                        onChange={handleRadioChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label htmlFor="emi-normal" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                        Normal EMI
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="emi-advance"
                        name="emiPayment"
                        type="radio"
                        value="Advance EMI"
                        checked={formData.emiPayment === 'Advance EMI'}
                        onChange={handleRadioChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label htmlFor="emi-advance" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                        Advance EMI
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={calculateEMI}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate EMI
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {results.showResults ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                  EMI Calculation Results
                </h2>

                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                    <div className="text-sm font-medium text-blue-800 dark:text-blue-200">Monthly EMI</div>
                    <div className="mt-1 text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(results.emi)}
                    </div>
                    <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                      {formData.emiPayment === 'Advance EMI' ? 'Paid at the beginning of each month' : 'Paid at the end of each month'}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Principal Amount</div>
                      <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(parseFloat(formData.loanAmount))}
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Interest</div>
                      <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(results.totalInterest)}
                        <span className={`ml-2 text-sm ${results.totalInterest > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                          ({((results.totalInterest / parseFloat(formData.loanAmount)) * 100).toFixed(1)}% of loan amount)
                        </span>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Payment</div>
                      <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(results.totalPayment)}
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Loan Tenure
                      </div>
                      <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {formData.loanTenure} {formData.loanTenureType}
                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                          ({(formData.loanTenureType === 'years' ? parseFloat(formData.loanTenure) * 12 : parseFloat(formData.loanTenure)).toLocaleString()} months)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <Info className="h-4 w-4 mr-1" />
                      <span>Interest rate of {formData.interestRate}% p.a. is used for calculation</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
                <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
                  <Calculator className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">EMI Calculator</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Enter your loan details and click "Calculate EMI" to see your monthly payment
                </p>
              </div>
            )}

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded-r">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Info className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">About EMI Calculation</h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                    <p>• EMI = P × r × (1 + r)^n / [(1 + r)^n - 1]</p>
                    <p>• P = Principal loan amount</p>
                    <p>• r = Monthly interest rate (annual rate/12/100)</p>
                    <p>• n = Loan term in months</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informational Content */}
      <div className="mt-12">
        <InformationalContent
          title={emiCalculatorContent.title}
          description={emiCalculatorContent.description}
          content={emiCalculatorContent.content}
          faqs={emiCalculatorContent.faqs}
          relatedTools={emiCalculatorContent.relatedTools}
        />
      </div>
    </div>
  );
}
